import type { AxisDateTimeLabelFormatsOptions } from 'highcharts';

/**
 * Highcharts `datetime` axes use x values in **milliseconds** since the Unix
 * epoch. Many backends (ClickHouse, APIs) return **seconds**; those look
 * like wrong 1970-era labels or show raw large numbers.
 *
 * - Values >= 1e12 are treated as **milliseconds** and left unchanged.
 * - Values **above max 32-bit UNIX seconds** (~Feb 2106) cannot be epoch
 *   seconds; they are treated as **milliseconds** (prevents ms in [1e8, 1e12)
 *   for ~1970–2001 from being multiplied again).
 * - Remaining values in **[1e8, max posix seconds]** are treated as **seconds**
 *   and converted to ms.
 * - Smaller values are left as-is (e.g. category indices).
 */
const MAX_POSIX_SECONDS = 4294967295;

export function toHighchartsDateTimeMs(x: number): number {
	if (typeof x !== 'number' || !Number.isFinite(x) || x <= 0) {
		return x;
	}
	if (x >= 1e12) {
		return x;
	}
	if (x > MAX_POSIX_SECONDS) {
		return x;
	}
	if (x >= 1e8) {
		return x * 1000;
	}
	return x;
}

/**
 * Coalesces a series point x for datetime axes: numbers (seconds or ms) and
 * date strings. Numeric **strings** of epoch seconds (or ms) are handled.
 */
export function coalesceLineChartX(x: number | string): number | string {
	if (typeof x === 'string') {
		const trimmed = x.trim();
		if (/^\d+(\.\d+)?$/.test(trimmed)) {
			return toHighchartsDateTimeMs(Number(trimmed));
		}
		const t = new Date(x).getTime();
		if (!Number.isNaN(t)) {
			return t;
		}
		return x;
	}
	return toHighchartsDateTimeMs(x);
}

const epochMsFormatter = new Intl.DateTimeFormat(undefined, {
	year: 'numeric',
	month: 'short',
	day: 'numeric',
});

/**
 * For bar/column **category** axes, turns numeric epoch (seconds or ms) into
 * a short local date; non-dates use string rules.
 */
export function formatCategoryIfEpochValue(raw: unknown): string {
	if (raw === null || raw === undefined) {
		return '';
	}
	if (typeof raw === 'string') {
		const trimmed = raw.trim();
		if (trimmed.toLowerCase() === 'null') {
			return '';
		}
		if (/^\d+(\.\d+)?$/.test(trimmed)) {
			return formatCategoryIfEpochValue(Number(trimmed));
		}
		return trimmed;
	}
	if (typeof raw === 'number' && Number.isFinite(raw)) {
		if (raw > 0 && raw < 1e7) {
			return String(raw);
		}
		const ms =
			raw >= 1e12
				? raw
				: raw >= 1e8
					? toHighchartsDateTimeMs(raw)
					: raw;
		if (ms >= 1e10) {
			const d = new Date(ms);
			if (!Number.isNaN(d.getTime())) {
				return epochMsFormatter.format(d);
			}
		}
	}
	const normalized = String(raw).trim();
	return normalized.toLowerCase() === 'null' ? '' : normalized;
}

/**
 * Highcharts v13 boundary-aware datetime axis labels for cartesian charts.
 * Requires Highcharts >= 13 at runtime (`boundary` on dateTimeLabelFormats).
 * @see https://www.highcharts.com/blog/post/highcharts-v13-is-here/
 */
export const JEPTO_CARTESIAN_DATETIME_LABEL_FORMATS = {
	millisecond: { main: '%b %e' },
	second: { main: '%b %e' },
	minute: { main: '%b %e' },
	hour: { main: '%b %e' },
	day: { main: '%e', boundary: '%b %e' },
	week: { main: '%e %b', boundary: '%b %Y' },
	month: { main: '%b', boundary: '%b %Y' },
	year: { main: '%Y', boundary: '%b %Y' },
} as AxisDateTimeLabelFormatsOptions;
